import { Box } from '@chakra-ui/layout'
import { useToast } from '@chakra-ui/react'
import { TableBuild } from 'components/organisms'
import { useInstitutionsContext } from 'contexts/institutions'
import { motion } from 'framer-motion'
import { ForwardRefRenderFunction, forwardRef, memo, useEffect, useImperativeHandle, useState } from 'react'
import { getAccessLogs } from 'services/users'
import formatter from 'utils/formatter'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import { accessReportHeaders } from '../../constants'
import { AccessFilter, AccessLog } from '../../types'
import { defaultLoader } from './constants'

interface IPropsModelTable {
    filter: AccessFilter 
}

interface IModeTableRef {
    resetPage: () => void
}

const ModeTableFC: ForwardRefRenderFunction<IModeTableRef, IPropsModelTable> = (
    { filter }, refModeTable
) => {
    const toast = useToast()
    const [loading, setLoading] = useState(false)
    const [totalPages, setTotalPages] = useState(1)
    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState(10)
    const { currentInstitution } = useInstitutionsContext()
    const [table, setTable] = useState({ ...accessReportHeaders, body: [] })

    useImperativeHandle(refModeTable, () => ({
        resetPage: () => setPage(1)
    }))

    const buildTableBody = (accessLogs: AccessLog[]) => {
        return accessLogs.map(log => ({
            ...log,
            cpf: formatter.cpf(log.cpf),
            dataAcesso: formatter.dataWithHour(log.dataAcesso)
        }))
    }

    const buildPayload = () => {
        const payload = removeEmptyValues({
            pagina: page,
            limite: limit,
            ...filter
        })

        return payload
    }

    async function fetchAccessLogs() {
        setLoading(true)
        try {
            const response = await getAccessLogs(buildPayload(), currentInstitution?.id)
            
            setTotalPages(+response.headers['total-pages'])

            const logs = response.data || []

            setTable((old: any) => ({
                ...old,
                body: buildTableBody(logs) 
            }))
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu algum erro ao buscar os dados, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
            setTable(old => ({ ...old, body: [] }))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchAccessLogs()
    }, [page, limit, filter, currentInstitution?.id])

    const handlePage = (value: number) => {
        if (page !== value) {
            setPage(value)
        }
    }

    const handleLimit = (value: number) => {
        setLimit(value)
        setPage(1)
    }

    return (
        <>
            <Box
                as={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <TableBuild
                    header={table.header}
                    body={table.body}
                    loading={{
                        status: loading,
                        loader: defaultLoader
                    }}
                    pagination={{
                        page,
                        limit,
                        totalPages
                    }}
                    onChangePage={handlePage}
                    onChangeLimit={handleLimit}
                ></TableBuild>
            </Box>
        </>
    )
}

export const ModeTable = memo(forwardRef(ModeTableFC))